import type { Turma } from '../../../types/models';
import {
  professorOcupadoNoSlot,
  type OcupacaoProfessorSlot,
} from '../grade-conflito';

export type GradeStatusKind = 'SEM_GRADE' | 'GAPS' | 'CONFLITO' | 'OK';

export type TurmaHorarioResumo = {
  diaSemana: string;
  numeroAula?: number;
  horaInicio?: string | null;
  horaFim?: string | null;
  professorId?: string | null;
  materiaId?: string | null;
};

export type GradeTurmaAvaliacao = {
  kind: GradeStatusKind;
  label: string;
  totalSlots: number;
  comProfessor: number;
  semProfessor: number;
  semMateria: number;
  semAlunos: boolean;
  detalhe: string;
};

function horariosDaTurma(t: Turma): TurmaHorarioResumo[] {
  return ((t as Turma & { horarios?: TurmaHorarioResumo[] }).horarios ?? []) as TurmaHorarioResumo[];
}

function turmaTemConflito(
  t: Turma,
  horarios: TurmaHorarioResumo[],
  ocupacao: OcupacaoProfessorSlot[],
): boolean {
  if (!ocupacao.length) return false;
  const turnoOpId = t.turnoOperacionalId ?? t.turnoOperacional?.id ?? null;
  for (const h of horarios) {
    if (!h.professorId) continue;
    const hit = professorOcupadoNoSlot(ocupacao, {
      professorId: h.professorId,
      diaSemana: h.diaSemana,
      numeroAula: h.numeroAula ?? 0,
      horaInicio: h.horaInicio,
      horaFim: h.horaFim,
      excludeTurmaId: t.id,
      turnoOperacionalId: turnoOpId,
    });
    if (hit) return true;
  }
  return false;
}

/** Avalia saúde da grade da turma para chips na listagem. */
export function avaliarGradeTurma(
  t: Turma,
  ocupacao: OcupacaoProfessorSlot[] = [],
): GradeTurmaAvaliacao {
  const horarios = horariosDaTurma(t);
  const totalSlots = horarios.length;
  const semProfessor = horarios.filter((h) => !h.professorId).length;
  const semMateria = horarios.filter((h) => !h.materiaId).length;
  const comProfessor = totalSlots - semProfessor;
  const semAlunos = (t._count?.alunos ?? 0) === 0;

  if (totalSlots === 0) {
    return {
      kind: 'SEM_GRADE',
      label: 'Sem grade',
      totalSlots,
      comProfessor,
      semProfessor,
      semMateria,
      semAlunos,
      detalhe: 'Nenhum horário configurado — abra o assistente de grade',
    };
  }

  const gaps = semProfessor > 0 || semMateria > 0;
  const conflito = !gaps && turmaTemConflito(t, horarios, ocupacao);

  if (conflito) {
    return {
      kind: 'CONFLITO',
      label: 'Conflito',
      totalSlots,
      comProfessor,
      semProfessor,
      semMateria,
      semAlunos,
      detalhe: 'Professor alocado em horário sobreposto em outra turma',
    };
  }

  if (gaps) {
    const partes: string[] = [];
    if (semProfessor > 0) partes.push(`${semProfessor} sem professor`);
    if (semMateria > 0) partes.push(`${semMateria} sem matéria`);
    return {
      kind: 'GAPS',
      label: 'Gaps',
      totalSlots,
      comProfessor,
      semProfessor,
      semMateria,
      semAlunos,
      detalhe: `${comProfessor}/${totalSlots} ok · ${partes.join(' · ')}`,
    };
  }

  return {
    kind: 'OK',
    label: 'Grade OK',
    totalSlots,
    comProfessor,
    semProfessor,
    semMateria,
    semAlunos,
    detalhe: `${totalSlots} slots com matéria e professor`,
  };
}
